import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  fullDescription: string;
  image: string;
  gallery: string[];
  specs: {
    size: string;
    duration: string;
    materials: string;
    features: string[];
  };
  client: string;
  location: string;
  year: string;
  featured: boolean;
}

const projects: Project[] = [
  {
    id: "modern-luxury-home",
    title: "Modern Luxury Home - Newport Beach",
    category: "Residential",
    description: "Contemporary rough framing for a stunning 5,000 sq ft luxury residence with complex architectural elements",
    fullDescription: "This modern luxury home project in Newport Beach showcased our expertise in complex architectural rough framing. The project featured soaring 20-foot ceilings, custom engineered trusses, and intricate roof framing that required precise engineering and craftsmanship. We worked closely with the architect to ensure every structural detail was executed to perfection, completing the rough framing 20% faster than industry average while maintaining the highest quality standards.",
    image: "/images/project-luxury-home.jpg",
    gallery: [
      "/images/project-luxury-home-1.jpg",
      "/images/project-luxury-home-2.jpg",
      "/images/project-luxury-home-3.jpg",
      "/images/project-luxury-home-4.jpg"
    ],
    specs: {
      size: "5,000 sq ft",
      duration: "8 weeks",
      materials: "Premium Douglas Fir, Engineered Lumber, Steel Beams",
      features: ["Custom Engineered Trusses", "20-ft Vaulted Ceilings", "Complex Roof Design", "Energy Efficient", "Seismic Compliance"]
    },
    client: "Private Residence",
    location: "Newport Beach, CA",
    year: "2024",
    featured: true
  },
  {
    id: "downtown-office-complex",
    title: "Downtown Irvine Office Complex",
    category: "Commercial",
    description: "Multi-story commercial rough framing with modern architectural elements and LEED certification requirements",
    fullDescription: "This downtown Irvine office complex required sophisticated rough framing techniques to accommodate the building's unique architectural design and LEED certification requirements. The project included three stories with varying floor plans, large open spaces, and structural elements that needed to support heavy loads while maintaining aesthetic appeal. We completed the rough framing phase 15% under budget and two weeks ahead of schedule, earning praise from the general contractor and architect.",
    image: "/images/project-office-complex.jpg",
    gallery: [
      "/images/project-office-1.jpg",
      "/images/project-office-2.jpg",
      "/images/project-office-3.jpg",
      "/images/project-office-4.jpg"
    ],
    specs: {
      size: "25,000 sq ft",
      duration: "12 weeks",
      materials: "Steel Frame, Engineered Beams, Sustainable Lumber",
      features: ["Multi-Story", "Open Floor Plans", "Structural Steel", "Modern Design", "LEED Certified", "Seismic Bracing"]
    },
    client: "Irvine Development Corp",
    location: "Irvine, CA",
    year: "2023",
    featured: true
  },
  {
    id: "custom-deck-pergola",
    title: "Custom Deck & Pergola - Laguna Beach",
    category: "Custom",
    description: "Handcrafted outdoor living space with premium wood rough framing and custom architectural elements",
    fullDescription: "This custom outdoor living project in Laguna Beach combined traditional woodworking techniques with modern construction methods. The pergola features hand-carved details and custom joinery, while the deck incorporates sustainable materials and innovative drainage systems. The project required precise rough framing to support the complex architectural design while withstanding coastal weather conditions. We completed the project 25% faster than estimated while exceeding quality expectations.",
    image: "/images/project-deck-pergola.jpg",
    gallery: [
      "/images/project-deck-1.jpg",
      "/images/project-deck-2.jpg",
      "/images/project-deck-3.jpg",
      "/images/project-deck-4.jpg"
    ],
    specs: {
      size: "800 sq ft",
      duration: "3 weeks",
      materials: "Cedar, Pressure Treated Lumber, Marine-Grade Hardware",
      features: ["Custom Joinery", "Hand-Carved Details", "Integrated Lighting", "Drainage System", "Coastal Weather Resistant", "Seismic Bracing"]
    },
    client: "Private Residence",
    location: "Laguna Beach, CA",
    year: "2024",
    featured: true
  },
  {
    id: "retail-space-framing",
    title: "Huntington Beach Retail Space",
    category: "Commercial",
    description: "Efficient rough framing solution for modern retail environment with flexible layout requirements",
    fullDescription: "This Huntington Beach retail space project required flexible rough framing that could accommodate various store layouts and future modifications. We implemented modular framing techniques that allow for easy reconfiguration while maintaining structural integrity. The project was completed 30% under budget and one week ahead of schedule, allowing the retail tenant to open earlier than planned.",
    image: "/images/project-retail-space.jpg",
    gallery: [
      "/images/project-retail-1.jpg",
      "/images/project-retail-2.jpg",
      "/images/project-retail-3.jpg",
      "/images/project-retail-4.jpg"
    ],
    specs: {
      size: "8,000 sq ft",
      duration: "6 weeks",
      materials: "Engineered Lumber, Steel Supports, Modular Components",
      features: ["Modular Design", "Flexible Layout", "Load Bearing", "Future-Ready", "Quick Assembly", "Cost Effective"]
    },
    client: "Huntington Beach Retail Chain",
    location: "Huntington Beach, CA",
    year: "2023",
    featured: false
  },
  {
    id: "traditional-home-addition",
    title: "Traditional Home Addition - Anaheim",
    category: "Residential",
    description: "Seamless integration of new rough framing with existing structure in historic district",
    fullDescription: "This home addition project in Anaheim's historic district required careful planning to match the existing structure's framing style and materials. We seamlessly integrated the new addition while preserving the home's architectural integrity and ensuring structural compatibility. The project required special permits and careful coordination with the historic preservation board. We completed the rough framing phase with zero damage to the existing structure and received commendation from the city's historic preservation committee.",
    image: "/images/project-home-addition.jpg",
    gallery: [
      "/images/project-addition-1.jpg",
      "/images/project-addition-2.jpg",
      "/images/project-addition-3.jpg",
      "/images/project-addition-4.jpg"
    ],
    specs: {
      size: "1,200 sq ft",
      duration: "4 weeks",
      materials: "Matching Existing Materials, Historic-Compliant Lumber",
      features: ["Seamless Integration", "Structural Compatibility", "Architectural Match", "Minimal Disruption", "Historic Preservation", "Permit Compliance"]
    },
    client: "Private Residence",
    location: "Anaheim, CA",
    year: "2024",
    featured: false
  },
  {
    id: "warehouse-framing",
    title: "Santa Ana Industrial Warehouse",
    category: "Commercial",
    description: "Large-scale industrial rough framing for warehouse facility with heavy equipment support",
    fullDescription: "This Santa Ana industrial warehouse project required robust rough framing to support heavy equipment and storage systems. We implemented advanced framing techniques to maximize space efficiency while ensuring structural integrity for long-term use. The project featured 40-foot clear spans and specialized framing to support overhead cranes and heavy machinery. We completed the rough framing phase 20% faster than industry average while maintaining the highest safety standards.",
    image: "/images/project-warehouse-new.jpg",
    gallery: [
      "/images/project-warehouse-1.jpg",
      "/images/project-warehouse-2.jpg",
      "/images/project-warehouse-3.jpg",
      "/images/project-warehouse-4.jpg"
    ],
    specs: {
      size: "50,000 sq ft",
      duration: "16 weeks",
      materials: "Heavy Timber, Steel Frame, Industrial Hardware",
      features: ["High Load Capacity", "Clear Span Design", "Equipment Support", "Efficient Layout", "Crane Support", "Seismic Compliance"]
    },
    client: "Santa Ana Manufacturing Company",
    location: "Santa Ana, CA",
    year: "2023",
    featured: false
  }
];

const categories = ["All Projects", "Residential", "Commercial", "Custom"];

const Projects: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Projects");
  const [currentProject, setCurrentProject] = useState(0);

  const filteredProjects = projects.filter(project => 
    selectedCategory === "All Projects" || project.category === selectedCategory
  );

  const featuredProjects = projects.filter(project => project.featured);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentProject((prev) => (prev + 1) % featuredProjects.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [featuredProjects.length]);

  const nextProject = () => setCurrentProject((prev) => (prev + 1) % featuredProjects.length);
  const prevProject = () => setCurrentProject((prev) => (prev - 1 + featuredProjects.length) % featuredProjects.length);

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Projects | Rough Framing Co. | Our Portfolio & Work</title>
        <meta name="description" content="Explore our portfolio of framing projects including residential, commercial, and custom wood structures. See our craftsmanship in action." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/projects-hero.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Our Projects
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              Showcasing our finest work and exceptional craftsmanship
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects Carousel */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Featured Projects
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Our most impressive and challenging projects
            </p>
          </div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${currentProject * 100}%)` }}>
                {featuredProjects.map((project, index) => (
                  <div key={project.id} className="w-full flex-shrink-0 relative">
                    <div 
                      className="h-[600px] bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${project.image})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                      <div className="absolute bottom-16 left-16 right-16 text-white">
                        <span className="bg-amber-800 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 inline-block border border-amber-600">
                          {project.category}
                        </span>
                        <h3 className="text-4xl font-bold mb-4">{project.title}</h3>
                        <p className="text-gray-200 text-xl max-w-2xl mb-6">{project.description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div>
                            <div className="text-amber-400 font-semibold">Size</div>
                            <div className="text-gray-300">{project.specs.size}</div>
                          </div>
                          <div>
                            <div className="text-amber-400 font-semibold">Duration</div>
                            <div className="text-gray-300">{project.specs.duration}</div>
                          </div>
                          <div>
                            <div className="text-amber-400 font-semibold">Location</div>
                            <div className="text-gray-300">{project.location}</div>
                          </div>
                          <div>
                            <div className="text-amber-400 font-semibold">Year</div>
                            <div className="text-gray-300">{project.year}</div>
                          </div>
                        </div>
                        <Link to={`/projects/${project.id}`}>
                          <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600">
                            View Case Study
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={prevProject}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Previous project"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextProject}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Next project"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="flex justify-center mt-12 space-x-4">
            {featuredProjects.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentProject(index)}
                className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                  index === currentProject ? "bg-amber-600 border-amber-600 scale-125" : "bg-transparent border-gray-400 hover:border-white"
                }`}
                aria-label={`Go to project ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-black border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-amber-800 text-white shadow-2xl border border-amber-600"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* All Projects Grid */}
      <section className="py-24 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/commercial-framing.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Our Projects
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Showcasing our finest work and exceptional craftsmanship
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-gray-800 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="h-64 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${project.image})` }}>
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                  {project.featured && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-800 text-white px-3 py-1 rounded-full text-sm font-semibold border border-amber-600">
                        Featured
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-gray-700 text-amber-400 px-3 py-1 rounded-full text-sm font-semibold">
                      {project.category}
                    </span>
                    <span className="text-gray-400 text-sm">{project.year}</span>
                  </div>
                  <h3 className="text-xl font-bold text-amber-400 mb-3 group-hover:text-amber-300 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div>
                      <div className="text-amber-400 font-semibold">Size</div>
                      <div className="text-gray-300">{project.specs.size}</div>
                    </div>
                    <div>
                      <div className="text-amber-400 font-semibold">Duration</div>
                      <div className="text-gray-300">{project.specs.duration}</div>
                    </div>
                  </div>
                  <Link to={`/projects/${project.id}`}>
                    <button className="w-full bg-amber-800 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600">
                      View Case Study
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Statistics */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Project Statistics
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Our track record of successful projects
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-6xl font-bold text-amber-400 mb-4">
                500+
              </div>
              <div className="text-gray-300 text-lg font-semibold">
                Projects Completed
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-6xl font-bold text-amber-400 mb-4">
                25+
              </div>
              <div className="text-gray-300 text-lg font-semibold">
                Years Experience
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-6xl font-bold text-amber-400 mb-4">
                100%
              </div>
              <div className="text-gray-300 text-lg font-semibold">
                Client Satisfaction
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-6xl font-bold text-amber-400 mb-4">
                50+
              </div>
              <div className="text-gray-300 text-lg font-semibold">
                Team Members
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Get Your Free Quote?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Let's discuss your framing needs and create something exceptional together. 
            Get a free consultation and quote for your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
            <a href="tel:+1234567890">
              <button className="bg-transparent hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 border-2 border-amber-600 hover:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-600/50">
                Call (123) 456-7890
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects; 